import fallbackRegions from "./fallback-regions.json";
import fallbackRegionCountries from "./fallback-region-countries.json";
import { resolveApiBaseUrl } from "./apiBaseUrl";

// Used from both middleware.ts (server/edge) and browser components
// (RegionGuard, Navbar, Footer, ...) — resolveApiBaseUrl() picks the right
// host for whichever is calling.
const BASE_URL = resolveApiBaseUrl() || "http://localhost:8000";

let cachedRegionCodes: string[] = fallbackRegions;

/**
 * ISO 3166-1 alpha-2 country code (lowercase) -> the slug of the region that
 * serves it, derived from the same /api/regions payload as the slug list.
 * Empty until an admin assigns countries to a region (`countries.region_id`);
 * an unmapped country is simply not geolocated, never guessed at.
 */
let cachedCountryRegions: Record<string, string> = fallbackRegionCountries;
let lastFetchTime = 0;
const CACHE_TTL = 60 * 1000; // 1 minute cache TTL
let isFetching = false;

/**
 * Initiates an asynchronous background fetch to update the cached regions list.
 */
async function revalidateRegionsInBackground(): Promise<void> {
  if (isFetching) return;
  isFetching = true;

  try {
    const res = await fetch(`${BASE_URL}/api/regions`, {
      headers: {
        "Accept": "application/json",
      },
    });
    if (!res.ok) {
      throw new Error(`Failed to fetch regions: ${res.status}`);
    }
    const json = await res.json();
    const codes: string[] = [];
    const countryRegions: Record<string, string> = {};
    if (json && Array.isArray(json.regions)) {
      for (const region of json.regions) {
        if (!region.slug) {
          continue;
        }

        const slug = region.slug.toLowerCase();
        codes.push(slug);

        // The same payload already carries each region's countries (see the
        // backend's RegionController), so the geolocation map costs no extra
        // request and can never drift from the region list beside it.
        for (const country of Array.isArray(region.countries) ? region.countries : []) {
          if (typeof country?.code === "string" && country.code.trim() !== "") {
            countryRegions[country.code.trim().toLowerCase()] = slug;
          }
        }
      }
    }
    cachedRegionCodes = codes;
    cachedCountryRegions = countryRegions;
    lastFetchTime = Date.now();
  } catch (error) {
    console.error("Error revalidating regions in background:", error);
    // Keep using the existing cache (or fallback) on failure
  } finally {
    isFetching = false;
  }
}

/**
 * Returns supported region codes synchronously from memory,
 * and initiates revalidation in the background if the cache is stale.
 * Supports NextFetchEvent.waitUntil to prevent Edge middleware from shutting down prematurely.
 */
export function getSupportedRegions(waitUntil?: (promise: Promise<void>) => void): string[] {
  const now = Date.now();
  
  // If cache is expired, trigger background revalidation
  if (now - lastFetchTime >= CACHE_TTL) {
    const revalidatePromise = revalidateRegionsInBackground();
    if (waitUntil) {
      waitUntil(revalidatePromise);
    }
  }

  return cachedRegionCodes;
}

/**
 * The region serving a visitor's country, from the ISO 3166-1 alpha-2 code
 * Cloudflare puts on the request (`CF-IPCountry`). Used by proxy.ts to send a
 * first-time visitor to their region's URL instead of the region-less one,
 * which the whole app reads as Global.
 *
 * Returns null — meaning "leave them where they are" — for anything it cannot
 * answer confidently: no header, a country no region claims, and Cloudflare's
 * own non-country values (`XX` for unknown, `T1` for Tor) which simply never
 * appear in the map. Never guesses.
 */
export function getRegionForCountry(
  countryCode: string | null | undefined,
  waitUntil?: (promise: Promise<void>) => void,
): string | null {
  if (!countryCode) {
    return null;
  }

  const now = Date.now();

  if (now - lastFetchTime >= CACHE_TTL) {
    const revalidatePromise = revalidateRegionsInBackground();
    if (waitUntil) {
      waitUntil(revalidatePromise);
    }
  }

  const slug = cachedCountryRegions[countryCode.trim().toLowerCase()];

  // Global is the absence of a region segment, so it is never a redirect target
  // — the region-less URL the visitor is already on IS the Global one.
  return slug && !isGlobalRegionSlug(slug) ? slug : null;
}

/**
 * Validates if the given region code is supported.
 */
export function isValidRegion(region?: string): boolean {
  if (!region) return false;
  const supported = getSupportedRegions();
  return supported.includes(region.toLowerCase());
}

/**
 * The slug of THE Global region (backend `regions.type = 'global'`). Mirrors the
 * backend's `GLOBAL_REGION_SENTINEL` string.
 *
 * Global is a real Region row, but the canonical PUBLIC URL for a Global user is
 * locale-only (`/en`, `/ar`) — Global is represented by the *absence* of a region
 * segment, never by its slug. So this slug must be treated exactly like "no
 * region" wherever a public URL is constructed. It is deliberately kept out of
 * the public region switcher (`/api/regions`), so it never appears in
 * `getSupportedRegions()` either.
 */
export const GLOBAL_REGION_SLUG = "global";

/**
 * Whether the given region slug identifies the Global region, which must never
 * be emitted as a URL segment.
 */
export function isGlobalRegionSlug(slug?: string | null): boolean {
  return !!slug && slug.toLowerCase() === GLOBAL_REGION_SLUG;
}

/**
 * The URL-safe region slug a signed-in user contributes, or null when they
 * contribute none: a guest, a user whose region has not loaded yet, or a Global
 * user — Global is the *absence* of a region segment, never "/global", so it has
 * to read as "no region" exactly like a guest does.
 *
 * The URL stays the source of truth for the region everywhere. This is only the
 * fallback for a pathname carrying no region segment at all, where "no segment"
 * would otherwise be read as Global even for a user who plainly belongs to one.
 */
export function getUserRegionSlug(user: unknown): string | null {
  const region = (user as { region?: { slug?: string | null } | null } | null | undefined)?.region;
  const slug = typeof region?.slug === "string" ? region.slug.toLowerCase() : null;

  return slug && !isGlobalRegionSlug(slug) ? slug : null;
}



// Locales are a fixed, known set, so they are deterministic on both the server
// and the client. Region detection from a pathname must NOT depend on the async
// region cache (getSupportedRegions): its contents differ between the server
// (already fetched the live list, which includes e.g. "eg") and the client at
// hydration time (still the static fallback list, which does not). Deriving the
// region from that cache produced server/client hydration mismatches on
// region-prefixed URLs (server -> /eg/en, client -> /en).
const LOCALES = ["en", "ar"];

/**
 * Extracts the region code on the client side from the URL pathname.
 *
 * Detected structurally rather than against the region cache: the first segment
 * is the region iff it is not itself a locale and the following segment is a
 * locale (`/{region}/{locale}/...`). This is deterministic across server and
 * client, which is what keeps SSR and hydration in agreement.
 */
export function getClientRegion(pathname: string): string | null {
  const segments = pathname.split("/").filter(Boolean);
  const [first, second] = segments;
  if (
    first &&
    !LOCALES.includes(first) &&
    second &&
    LOCALES.includes(second)
  ) {
    return first.toLowerCase();
  }
  return null;
}



/**
 * Resolves the region and locale from a given URL pathname.
 * Useful in middleware or layouts.
 */
export function resolveRegionAndLocale(pathname: string, locales: string[]): {
  region: string | null;
  locale: string | null;
} {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0];
  const second = segments[1];

  // If first segment is a valid locale
  if (first && locales.includes(first)) {
    return { region: null, locale: first };
  }

  // If first segment is a valid region and second is a valid locale
  if (first && isValidRegion(first)) {
    if (second && locales.includes(second)) {
      return { region: first.toLowerCase(), locale: second };
    }
  }

  return { region: null, locale: null };
}

/**
 * Whether `url` is an absolute URL (`http://`, `https://`, or
 * protocol-relative `//host/...`) rather than an internal path. Internal
 * route helpers (`getRegionLocaleHref`) must never run an absolute URL
 * through locale/region prefixing - concatenating a prefix onto an already-
 * complete URL produces a broken, doubly-prefixed string regardless of
 * whether that URL happens to share this app's own origin or not.
 */
export function isAbsoluteUrl(url: string): boolean {
  return /^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(url);
}

/**
 * Generates a localized path that includes the region prefix, if any.
 *
 * The region to use is resolved with the following precedence:
 * 1. `explicitRegion`, when given - the authoritative region for the
 *    signed-in user (`user.region.slug`). Passing this avoids building a
 *    region-less URL that a later correction (e.g. RegionGuard) has to bounce
 *    off of, which is what turns a single post-auth navigation into a race
 *    between multiple `router.replace()` calls.
 * 2. Otherwise, whatever region prefix is already in `currentPathname` -
 *    preserves the region a visitor is already browsing under.
 *
 * The Global region is authoritative "no region": its slug never becomes a URL
 * segment, so a Global user (or an explicit Global slug) yields a locale-only
 * path (`/en/...`). When `explicitRegion` is Global it wins as "no region" and
 * does NOT fall through to the pathname-derived region.
 *
 * `targetPath` is assumed to be an internal path; an absolute URL passed in
 * by mistake is returned unchanged rather than prefixed (see isAbsoluteUrl) -
 * callers that may receive either (e.g. an admin-configured button/CTA URL)
 * should branch on isAbsoluteUrl themselves before deciding whether to route
 * internally at all, since an absolute URL calls for different navigation
 * entirely (window.open/location, not router.push).
 */
export function getRegionLocaleHref(
  currentPathname: string | null | undefined,
  locale: string,
  targetPath: string,
  explicitRegion?: string | null
): string {
  if (isAbsoluteUrl(targetPath)) {
    return targetPath;
  }

  const cleanPath = targetPath.startsWith("/") ? targetPath : `/${targetPath}`;

  const explicit = explicitRegion?.toLowerCase();
  const region = explicit
    ? (isGlobalRegionSlug(explicit) ? null : explicit)
    : (currentPathname ? getClientRegion(currentPathname) : null);

  if (region) {
    if (cleanPath === "/" || cleanPath === "") {
      return `/${region}/${locale}`;
    }
    return `/${region}/${locale}${cleanPath}`;
  }

  if (cleanPath === "/" || cleanPath === "") {
    return `/${locale}`;
  }
  return `/${locale}${cleanPath}`;
}
